/**
 * "Detalhes" beside the Conversa of a session (the owner's ask, 16/10): every event the AI reported
 * for this session, in order, each with its full payload — tools, commands, files, tokens, replies —
 * exactly as the server keeps it (already redacted of secrets on the way in). A filter narrows by
 * kind; "Copiar tudo" takes the whole log as text for a bug report. What is not kept is not invented: on the
 * full log level (a paid plan's choice in Telemetria e privacidade) it also has what the agent said on the way
 * (agent.said) and each command whole.
 */

import { useMemo, useState } from 'react'
import { ChevronRight, Copy } from 'lucide-react'
import { useI18n } from '../lib/i18n'
import { formatTime } from '../lib/format'
import type { TimelineEntry } from '../lib/types'

function payloadText(payload: Record<string, unknown> | null): string {
  if (!payload || Object.keys(payload).length === 0) return ''
  return JSON.stringify(payload, null, 2)
}

export function SessionLog({ timeline }: { timeline: TimelineEntry[] }) {
  const { t, locale } = useI18n()
  const [kind, setKind] = useState<string>('')
  const [open, setOpen] = useState<Record<string, boolean>>({})
  const [copied, setCopied] = useState(false)

  const kinds = useMemo(() => [...new Set(timeline.map((e) => e.eventType))].sort(), [timeline])
  const shown = kind ? timeline.filter((e) => e.eventType === kind) : timeline

  const copyAll = () => {
    const text = shown
      .map((e) => [`${e.occurredAt} ${e.eventType}${e.summary ? ` — ${e.summary}` : ''}`, payloadText(e.payload)].filter(Boolean).join('\n'))
      .join('\n\n')
    void navigator.clipboard?.writeText(text).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    })
  }

  if (timeline.length === 0) return <span className="tiny muted">{t('work.log.empty')}</span>

  return (
    <div className="stack stack--tight session-log">
      <div className="row row--between row--wrap">
        <select className="select select--sm" value={kind} onChange={(e) => setKind(e.target.value)} aria-label={t('work.log.filter')}>
          <option value="">{t('work.log.all', { count: timeline.length })}</option>
          {kinds.map((k) => (
            <option key={k} value={k}>{k}</option>
          ))}
        </select>
        <button type="button" className="btn btn--sm btn--ghost" onClick={copyAll}>
          <Copy size={13} /> {copied ? t('common.copied') : t('work.log.copyAll')}
        </button>
      </div>
      <ol className="session-log__list">
        {shown.map((entry) => {
          const body = payloadText(entry.payload)
          const isOpen = open[entry.id] === true
          return (
            <li key={entry.id} className="session-log__row">
              <button
                type="button"
                className="session-log__head"
                onClick={() => body && setOpen((all) => ({ ...all, [entry.id]: !isOpen }))}
                aria-expanded={body ? isOpen : undefined}
                disabled={!body}
              >
                <ChevronRight size={12} aria-hidden className={`session-log__chevron${isOpen ? ' is-open' : ''}${body ? '' : ' is-empty'}`} />
                <span className="tiny muted mono">{formatTime(entry.occurredAt, locale)}</span>
                <span className="tiny mono session-log__type">{entry.eventType}</span>
                {entry.summary && <span className="small session-log__summary">{entry.summary}</span>}
              </button>
              {isOpen && body && <pre className="session-log__payload tiny mono">{body}</pre>}
            </li>
          )
        })}
      </ol>
      <span className="tiny muted">{t('work.log.note')}</span>
    </div>
  )
}
